import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import {
  addGameSessionUpdatedListener,
  addSpinEndListener,
  addSpinStartListener,
  getGameVendor,
  removeGameSessionUpdatedListener,
  removeSpinEndListener,
  removeSpinStartListener
} from 'utils/gameEvents';

const DEFAULT_TIMEOUT = 4000;
const EVENT_TIMEOUT = 60000;
const DEFAULT_VALUE_FIELD = 'value';
const DEFAULT_SPIN_END_BUFFER_MS = 500;

const withPendingSpinEnd = options => BaseComponent => {
  const {
    name = DEFAULT_VALUE_FIELD,
    spinEndBufferMs = DEFAULT_SPIN_END_BUFFER_MS
  } = options || {};

  class PendingSpinEnd extends PureComponent {
    constructor(props) {
      super(props);

      const startingValue = props[name];
      this.state = {
        displayValue: startingValue
      };

      this.pendingValue = null;
      this.syncedToSpin = undefined;
      this.vendorDelay = DEFAULT_TIMEOUT;
      this.pendingTimeout = null;
      this.lastSpinEndTime = null;

      this.handlePendingTimeout = this.handlePendingTimeout.bind(this);
      this.handleSpinStart = this.handleSpinStart.bind(this);
      this.handleSpinEnd = this.handleSpinEnd.bind(this);
      this.handleGameSessionUpdated = this.handleGameSessionUpdated.bind(this);
    }

    componentDidMount() {
      addSpinStartListener(this.handleSpinStart);
      addSpinEndListener(this.handleSpinEnd);
      addGameSessionUpdatedListener(this.handleGameSessionUpdated);
    }

    componentWillUnmount() {
      clearTimeout(this.pendingTimeout);

      removeSpinStartListener(this.handleSpinStart);
      removeSpinEndListener(this.handleSpinEnd);
      removeGameSessionUpdatedListener(this.handleGameSessionUpdated);
    }

    componentWillReceiveProps(nextProps) {
      const nextValue = nextProps[name];
      const currentValue =
        this.pendingValue !== null
          ? this.pendingValue
          : this.state.displayValue;
      if (
        nextValue !== undefined &&
        nextValue !== null &&
        nextValue !== currentValue
      ) {
        const snap =
          currentValue === null ||
          currentValue === undefined ||
          (nextProps.snapDown && nextValue < currentValue) ||
          (this.lastSpinEndTime &&
            Date.now() - this.lastSpinEndTime < spinEndBufferMs);
        const timeout = snap
          ? 0
          : this.syncedToSpin
          ? EVENT_TIMEOUT
          : nextProps.delay || nextProps.delay === 0
          ? nextProps.delay
          : this.vendorDelay || this.vendorDelay === 0
          ? this.vendorDelay
          : DEFAULT_TIMEOUT;
        this.setPendingValue(nextValue, timeout);
      }
    }

    handleGameSessionUpdated(gameSession) {
      const vendor = gameSession && getGameVendor(gameSession.gameUrl);
      this.syncedToSpin = vendor && vendor.syncedToSpin;
      this.vendorDelay = vendor && vendor.delay;
    }

    setPendingValue(value, timeout = 0) {
      if (this.pendingValue !== null && this.pendingValue !== undefined) {
        // Trigger any existing pending value now before overwriting it
        this.applyPendingValue();
      }

      this.pendingValue = value;
      this.lastSpinEndTime = null;
      if (timeout) {
        this.pendingTimeout = setTimeout(this.handlePendingTimeout, timeout);
      } else {
        this.applyPendingValue();
      }
    }

    handlePendingTimeout() {
      if (this.syncedToSpin === true) {
        this.syncedToSpin = undefined;
      }
      this.applyPendingValue();
    }

    applyPendingValue() {
      if (
        this.pendingValue !== null &&
        this.pendingValue !== undefined &&
        this.state.displayValue !== this.pendingValue
      ) {
        this.setState({ displayValue: this.pendingValue });
      }
      clearTimeout(this.pendingTimeout);
      this.pendingTimeout = null;
      this.pendingValue = null;
    }

    handleSpinStart() {
      if (this.syncedToSpin === undefined) {
        this.syncedToSpin = true;
        if (this.pendingTimeout) {
          // Reset to use event timeout instead of normal delay
          clearTimeout(this.pendingTimeout);
          this.pendingTimeout = setTimeout(
            this.handlePendingTimeout,
            EVENT_TIMEOUT
          );
        }
      }
    }

    handleSpinEnd() {
      this.lastSpinEndTime = Date.now();
      this.applyPendingValue();
    }

    render() {
      const { delay, ...otherProps } = this.props;
      const { displayValue } = this.state;
      const childProps = { [name]: displayValue };
      return <BaseComponent {...otherProps} {...childProps} />;
    }
  }

  PendingSpinEnd.defaultProps = {
    snapDown: true
  };

  PendingSpinEnd.propTypes = {
    delay: PropTypes.number,
    snapDown: PropTypes.bool
  };

  return PendingSpinEnd;
};

export default withPendingSpinEnd;
